/**
 * `/maintenance` — shown in place of the rest of the site while
 * `site-config.json`'s `maintenance.enabled` window is active. See
 * `MaintenanceGate` for how a visitor ends up here, and
 * docs/announcements-maintenance.md for the full behavior.
 *
 * Deliberately not built on `<Layout>`: no main nav, no footer link grid, no ad
 * slots — a visitor here should have exactly two options, go home once it's
 * back, or reach support. It still shares the site's fonts, colors and
 * dark-mode variables because those live on `<html>` and `index.css`, not
 * inside `Layout` itself.
 */

import { Link } from 'react-router-dom'
import { FileText, MessageSquareWarning, Settings } from 'lucide-react'
import { SITE } from '@/config/site'
import { useSeo } from '@/seo/useSeo'
import siteConfig from '@/data/site-config.json'
import { getMaintenanceStatus } from '@/lib/maintenance'
import { formatIstDateTime } from '@/lib/istDate'

export const MaintenancePage = () => {
  useSeo()
  const status = getMaintenanceStatus(siteConfig.maintenance)
  const returnTime = status.endAtMs !== null ? formatIstDateTime(status.endAtMs) : null

  return (
    <div className="flex min-h-dvh flex-col items-center justify-center bg-base-200 px-4 py-12 text-center">
      <Link to="/" className="mb-8 flex items-center gap-2 text-lg font-bold tracking-tight">
        <FileText size={22} className="text-primary" aria-hidden="true" />
        {SITE.name}
      </Link>

      <Settings size={40} className="anim-slow-spin text-primary" aria-hidden="true" />

      <h1 className="mt-5 text-2xl font-bold sm:text-3xl">We&apos;ll be back soon</h1>
      <p className="mx-auto mt-3 max-w-md text-balance opacity-80">{status.message}</p>

      {returnTime && <p className="mt-4 text-sm font-medium opacity-70">Expected return: {returnTime}</p>}

      <Link to="/contact" className="btn btn-outline btn-sm mt-8 gap-2">
        <MessageSquareWarning size={14} aria-hidden="true" /> Contact Us
      </Link>
    </div>
  )
}

export default MaintenancePage
